<template>
  <!-- No select-all listener: with `selectAll` unset, PrimeVue's toggle-all goes through
       updateModel and emits `change` like any other pick. -->
  <MultiSelect
    v-model="model"
    :options
    optionLabel="label"
    optionValue="value"
    :filterFields="FILTER_FIELDS"
    :placeholder
    class="w-100"
    filter
    appendTo="body"
    :focusOnHover="false"
    :overlayStyle="hasGroups ? GROUPED_OVERLAY_STYLE : undefined"
    :scrollHeight="hasGroups ? '18rem' : undefined"
    @change="$emit('change')"
    @filter="searchQuery = $event.value"
  >
    <template #filtericon>
      <font-awesome-icon
        icon="search"
        class="text-muted pv-multiselect-filter-icon"
      />
    </template>
    <template v-if="hasGroups" #header>
      <div ref="header" class="device-group-multi-select__header">
        <template v-if="groupRows.length > 0">
          <div class="device-group-multi-select__label">
            {{ groupsTitle ?? $t("device_groups.filter.groups") }}
          </div>
          <DeviceGroupChecklist :rows="groupRows" @toggle="toggleOptionGroup" />
        </template>
        <div class="device-group-multi-select__label">
          {{ $t("device_groups.filter.devices") }}
        </div>
      </div>
    </template>
    <template #value="{ value, placeholder: shownPlaceholder }">
      <span
        v-if="value && value.length"
        class="fw-bold"
        :class="tone && `text-${tone}`"
      >
        {{ selectionLabel }}
      </span>
      <span v-else>{{ shownPlaceholder }}</span>
    </template>
    <template #option="{ option }">
      <div
        class="d-flex w-100 gap-2 align-items-center"
        :data-device-value="option.value"
      >
        <TechnologyIcon
          v-if="option.technology"
          :key="option.technology"
          :technology="option.technology"
          iconClass="fa-fw flex-shrink-0"
        />
        <span class="text-truncate">{{ option.label }}</span>
        <span
          v-if="option.description && option.description !== option.label"
          class="text-body-secondary device-group-multi-select__secondary text-truncate"
        >
          {{ option.description }}
        </span>
        <span
          v-if="groupLabelByValue.has(option.value)"
          class="ms-auto text-body-secondary device-group-multi-select__secondary device-group-multi-select__option-group text-truncate"
        >
          {{ groupLabelByValue.get(option.value) }}
        </span>
      </div>
    </template>
  </MultiSelect>
</template>

<script setup lang="ts">
import TechnologyIcon from "@/components/common/TechnologyIcon.vue";
import DeviceGroupChecklist, {
  type DeviceGroupChecklistRow,
} from "@/dashboard/production_strategy/device_groups/DeviceGroupChecklist.vue";
import { groupSelectionState, toggleGroup } from "@/utils/groupSelection";
import MultiSelect from "primevue/multiselect";
import { computed, nextTick, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

export interface DeviceSelectOption {
  label: string;
  value: string;
  /** A second identifier, e.g. the EIC, shown beside the label and matched by the search. */
  description?: string;
  /** A TechnologyIcon technology (e.g. "WIND"), shown before the label. */
  technology?: string;
}

/** A device group in the dropdown: ticking it selects these option values. */
export interface DeviceSelectGroup {
  label: string;
  values: string[];
}

/**
 * Picks devices, with a Groups section of tri-state checkboxes heading the options when `groups`
 * are given: ticking a group selects those of its values that are among the options, so the model
 * stays plain values. A partly selected group counts its selected values ("1 of 2 devices"). Each
 * option names its group, the search narrows the groups as well as the
 * options, and the field reads one group's name, "N groups" or "N devices".
 *
 * Used both as a form field and as a DataTable column filter: there, add `class="p-column-filter"`,
 * bind the slot's `filterModel.value` with `v-model` and call its `filterCallback()` on `change`.
 */
const props = withDefaults(
  defineProps<{
    options: DeviceSelectOption[];
    groups?: DeviceSelectGroup[];
    placeholder?: string;
    /** Heads the group checkboxes; "Groups" by default. */
    groupsTitle?: string;
    /** Colours the selection label, as the column filters do (danger flags an unapplied filter). */
    tone?: "primary" | "danger";
  }>(),
  {
    groups: () => [],
    placeholder: undefined,
    groupsTitle: undefined,
    tone: undefined,
  }
);

const emit = defineEmits<{
  change: [];
}>();

const model = defineModel<string[] | null>();

const { t } = useI18n();

const FILTER_FIELDS = ["label", "description"];

// The default 300px overlay (and 14rem list) leaves the options two rows below a Groups section and
// the search box; with groups the overlay grows to fit about eight options.
const GROUPED_OVERLAY_STYLE = { maxHeight: "min(600px, 60vh)" };

const header = useTemplateRef<HTMLElement>("header");

// PrimeVue's search text, so the search narrows the groups as well as the options.
const searchQuery = ref("");

const hasGroups = computed(() => props.groups.length > 0);

const optionValues = computed(() =>
  props.options.map((option) => option.value)
);

// Keyed by the group's index in `groups`: labels from different companies' groups can repeat.
const groupRows = computed<DeviceGroupChecklistRow[]>(() => {
  const query = searchQuery.value.trim().toLowerCase();
  const available = new Set(optionValues.value);
  const selected = new Set(model.value ?? []);
  return props.groups
    .map((group, index) => {
      const state = groupSelectionState(
        group.values,
        optionValues.value,
        model.value ?? []
      );
      return {
        key: index,
        label: group.label,
        countLabel:
          state === "partial"
            ? t("device_groups.picker.group_partial_count", {
                selected: group.values.filter((value) => selected.has(value))
                  .length,
                count: group.values.length,
              })
            : t(
                "device_groups.devices_count",
                { count: group.values.length },
                group.values.length
              ),
        state,
        disabled: !group.values.some((value) => available.has(value)),
      };
    })
    .filter((row) => row.label.toLowerCase().includes(query));
});

const groupLabelByValue = computed(() => {
  const labels = new Map<string, string>();
  for (const group of props.groups) {
    for (const value of group.values) labels.set(value, group.label);
  }
  return labels;
});

/**
 * A group's name when the selection is exactly one whole group, a count of whole groups when it is
 * several, the option's label when it is one device, and a device count otherwise.
 */
const selectionLabel = computed(() => {
  const selected = model.value ?? [];
  const selectedSet = new Set(selected);
  const wholeGroups = props.groups.filter(
    (group) =>
      group.values.length > 0 &&
      group.values.every((value) => selectedSet.has(value))
  );
  const covered = new Set(wholeGroups.flatMap((group) => group.values));
  if (wholeGroups.length === 1 && covered.size === selectedSet.size) {
    return wholeGroups[0].label;
  }
  if (wholeGroups.length > 1 && covered.size === selectedSet.size) {
    return t(
      "device_groups.filter.n_groups",
      { count: wholeGroups.length },
      wholeGroups.length
    );
  }
  if (selected.length === 1) {
    return (
      props.options.find((option) => option.value === selected[0])?.label ??
      selected[0]
    );
  }
  return t(
    "device_groups.devices_count",
    { count: selected.length },
    selected.length
  );
});

const toggleOptionGroup = async (key: number) => {
  const group = props.groups[key];
  // The new selection, read from here: `model.value` keeps the old one until the parent re-renders.
  const selection = toggleGroup(
    group.values,
    optionValues.value,
    model.value ?? []
  );
  model.value = selection;
  emit("change");
  if (group.values.some((value) => selection.includes(value))) {
    const overlay = header.value?.closest("[data-pc-section=overlay]") ?? null;
    await nextTick();
    scrollToFirstOptionOf(group, overlay);
  }
};

// Brings the group's first option to the top of the list, so ticking a group shows what it ticked.
// Options are found by the value the #option slot renders on them.
const scrollToFirstOptionOf = (
  group: DeviceSelectGroup,
  overlay: Element | null
) => {
  const values = new Set(group.values);
  const first = [
    ...(overlay?.querySelectorAll<HTMLElement>("[data-device-value]") ?? []),
  ]
    .find((content) => values.has(content.dataset.deviceValue ?? ""))
    ?.closest<HTMLElement>("[role=option]");
  const list = first?.closest<HTMLElement>("[role=listbox]");
  if (!first || !list) return;
  list.scrollTop +=
    first.getBoundingClientRect().top - list.getBoundingClientRect().top;
};
</script>

<style scoped lang="scss">
// Sits in the overlay's fixed-height flex column: keep the group rows from being squeezed away.
.device-group-multi-select__header {
  flex-shrink: 0;
}

.device-group-multi-select__label {
  padding: 8px 12px 4px;
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--phoenix-quaternary-color);
}

.device-group-multi-select__secondary {
  font-size: 0.72rem;
}

.device-group-multi-select__option-group {
  max-width: 50%;
}
</style>
